<template>
  <div class="QuestionRenderer">
    <div
      v-if="isSelected"
      class="Actions-menu u-flex u-flex-spaceBetween"
    >
      <span
        class="QuestionUuid"
      >{{ question.question_uuid }}</span>
      <div class="Buttons-group">
        <button
          v-if="canReplaceQuestion"
          class="ReplaceButton"
          @click="replaceQuestion"
        >
          <span
            class="Icon Icon-exchange-alt"
          />
        </button>
        <button
          class="RemoveButton"
          @click="removeQuestion(question)"
        >
          <span
            class="Icon-s Icon-trash"
          />
        </button>
      </div>
    </div>
    <div
      :class="[{'Selected' : isSelected},'Question-container']"
      @click="handleSelectQuestion"
    >
      <div class="Question-inputs">
        <span
          class="Icon-s Icon-grip-vertical handle"
        />
        <div class="Question-input-type">
          <div class="u-flex">
            <label
              v-if="!hideQuestionHeaderLabel"
              :class="['DynamicLabel', { 'Required': question.is_required }]"
              v-html="question.label"
            />

            <h2
              v-else-if="question.type === 'header'"
              class="HeaderText RichHtml"
              v-html=" question.label"
            />

            <div
              v-else-if="question.type === 'paragraph'"
              class="RichHtml"
              v-html="question.label"
            />
            <span
              v-if="question.is_hidden"
              class="Hint HiddenQuestion Icon-s Icon-eye-slash"
            />

            <tooltip
              v-if="questionHasConditionalLogic"
              class="ConditionalLogic-container"
            >
              <span
                slot="trigger"
                class="Table-action"
              >
                <span
                  slot="trigger"
                  class="ConditionalLogicIcon Icon-s Icon-ballot-check"
                />
              </span>
              <span
                slot="tip"
              > Shown if {{ conditionalLogicFieldLabel }}
                <span>{{ question.conditional_logic.constraint }}</span>
                <span v-if="question.conditional_logic.value"> {{ question.conditional_logic.value }}</span>
              </span>
            </tooltip>
          </div>

          <!-- QUESTION TYPES -->
          <div>
            <rmp-input
              v-if="question.type === 'text'"
              :model-data="question.value"
            />

            <rmp-input
              v-else-if="question.type === 'email'"
              input-type="email"
              :model-data="question.value"
            />

            <rmp-input
              v-else-if="question.type === 'tel'"
              :model-data="question.value"
            />

            <rmp-textarea
              v-else-if="question.type === 'long_text'"
              class="TextArea"
              :model-data="question.value"
            />

            <rmp-input
              v-else-if="question.type === 'number'"
              input-type="number"
            />

            <rmp-select
              v-else-if="question.type === 'select'"
              class="Form-field--select"
              :items="question.options ? question.options.map(q => q.label) : []"
              :model-data="question.value"
            />

            <rmp-star-rating
              v-else-if="question.type === 'rating'"
              :max-stars="question.options.range"
              :polar-adjectives="{
                good: question.options.max_rating_label,
                bad: question.options.min_rating_label
              }"
            />

            <rmp-autocomplete
              v-else-if="question.type === 'autocomplete'"
              class="Form-field--autocomplete"
              :items="question.options || []"
              :placeholder="question.value || null"
              option-key="label"
            />

            <rmp-datepicker
              v-else-if="question.type === 'date'"
              :model-data="question.value"
              :disable-dates="{from: null}"
            />

            <div
              v-else-if="question.type === 'time'"
              class="Form-group Time"
            >
              <rmp-select
                :model-data="timeDefaultValue.hour"
                :items="getNumberArray(1,23,1)"
                style="width: 100px; margin-right: 10px;"
                label="Hour"
              />
              <rmp-select
                :model-data="timeDefaultValue.minute"
                :items="getNumberArray(0,55,5)"
                style="width: 100px"
                label="Minutes"
              />
            </div>

            <!-- CUSTOM QUESTION TYPES -->

            <div
              v-else-if="question.type === 'radio'"
              class="radio-button-group"
            >
              <div
                v-for="(radio, index) in radioOptions"
                :key="index"
              >
                <rmp-radio-button
                  :label="radio.label"
                  :value="false"
                  name="radioName"
                />
              </div>
            </div>

            <div
              v-else-if="question.type === 'checkbox' ||
                question.type === 'checkboxOptGroup'"
            >
              <template
                v-if="isSingleSelectionCheckbox"
              >
                <rmp-checkbox
                  v-model="checkboxModel"
                  :html-label="question.label"
                  :value="question.value"
                  :required="question.is_required"
                />
              </template>
              <div v-else>
                <div
                  class="RichHtml"
                  v-html="question.hint"
                />
                <div
                  v-for="(checkbox, index) in questionOptionsList"
                  :key="index"
                >
                  <rmp-checkbox
                    :label="checkbox.label"
                    :checked="false"
                    :value="false"
                  />
                </div>
              </div>
            </div>

            <div v-else-if="question.type === 'file_upload'">
              <div
                class="FileUpload-container"
                style="margin-top: 10px"
              >
                <span class="Icon-s Icon-file-upload" />
                <p> Drag and drop or click here to upload a File</p>
                <p>Max file size is: {{ question.options.max_file_size_in_mb }}MB</p>
              </div>
              <hr style="margin: 10px 0;">
              <div style="display: flex;align-items: stretch; ">
                <div style="margin-right: 10px;">
                  Info:
                </div>
                <div style="margin-right: 10px;">
                  <span class="Icon-s Icon-file-word" /> Documents: {{ question.options.allows_documents ? 'Yes' : 'No' }}
                </div>
                <div style="margin-right: 10px;">
                  <span class="Icon-s Icon-camera" /> Images: {{ question.options.allows_images ? 'Yes' : 'No' }}
                </div>
                <div style="margin-right: 10px;">
                  <span class="Icon-s Icon-file-archive" /> Zip (Compressed files): {{ question.options.allows_zips ? 'Yes' : 'No' }}
                </div>
              </div>
            </div>

            <div
              v-if="question.type !== 'checkboxOptGroup'"
              class="RichHtml"
              v-html="question.hint"
            />
          </div>
          <!-- QUESTION TYPES:end -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import RmpInput from '@rmpenterprise/form-input';
import RmpSelect from '@rmpenterprise/form-select';
import RmpRadioButton from '@rmpenterprise/form-radio-button';
import RmpCheckbox from '@rmpenterprise/form-checkbox';
import RmpAutocomplete from '@rmpenterprise/form-autocomplete-select';
import RmpDatepicker from '@rmpenterprise/form-date-picker';
import RmpTextarea from '@rmpenterprise/form-text-area';
import RmpStarRating from '@rmpenterprise/star-rating';
import Tooltip from '../../common/tooltip.vue';

export default {
  components: {
    RmpInput,
    RmpRadioButton,
    RmpCheckbox,
    RmpSelect,
    RmpAutocomplete,
    RmpDatepicker,
    RmpTextarea,
    RmpStarRating,
    Tooltip,
  },
  props: {
    question: {
      type: Object,
      default: null,
    },
    showReplaceQuestion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checkboxlocalValue: false,
      nonQuestionElementTypes: ['header', 'paragraph'],
    };
  },
  computed: {
    // ...mapState('events', {
    //   questions: (state) => state.event.questions,
    // }),
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
    }),
    ...mapState('themes', {
      previewThemeCss: (state) => state.previewTheme,
    }),
    isSelected() {
      if (this.selectedQuestion.index === undefined) return false;
      return this.selectedQuestion.index === this.question.index;
    },
    checkboxModel: {
      get() {
        return !this.checkboxlocalValue;
      },
      set(val) {
        this.checkboxlocalValue = val;
      },
    },
    conditionalLogicFieldLabel() {
      // html_form_input_id is saved as the question uuid for conditional logic mapping

      // question uuid is mapped to html_form_input_id on the backend.
      // Return the label of the question for the tooltip conditional logic message.
      const [mappedQuestion] = this.formQuestions.filter((q) => {
        if (q.is_custom_question) return q.html_form_input_id === this.question.conditional_logic.related_to_question_uuid;
        return q.question_uuid === this.question.conditional_logic.related_to_question_uuid;
      });
      return this.stripHtmlTags(mappedQuestion.label);
    },
    canReplaceQuestion() {
      return this.showReplaceQuestion && this.question.list_of_questions_that_can_replace.length !== 0;
    },
    hideQuestionHeaderLabel() {
      // non question elements and single option checkboxes
      return this.nonQuestionElementTypes.indexOf(this.question.type) > -1
      || this.isSingleSelectionCheckbox;
    },
    isSingleSelectionCheckbox() {
      return ['checkbox', 'checkboxOptGroup'].indexOf(this.question.type) > -1
       && this.questionOptionsList.length === 0;
    },
    questionHasConditionalLogic() {
      if (this.question.conditional_logic) {
        if (Object.keys(this.question.conditional_logic).length === 3) {
          return Object.keys(this.question.conditional_logic).every((key) => this.question.conditional_logic[key] !== null);
        }
      }
      return false;
    },
    questionOptionsList() {
      return this.question.options || [];
    },
    radioOptions() {
      return this.question.options || [{ label: '' }];
    },
    timeDefaultValue() {
      if (this.question.type === 'time') {
        const splitTime = this.question.value ? this.question.value.split(':') : [];

        if (splitTime.length > 0) {
          return { hour: splitTime[0], minute: splitTime[1] };
        }

        return { hour: '', minute: '' };
      }
      return null;
    },
  },
  methods: {
    ...mapActions('forms', ['removeFormQuestion']),
    handleSelectQuestion() {
      // Emit edit for questions to become selected and editable
      this.$emit('question-renderer:select', this.question);
    },
    replaceQuestion() {
      // Do something
      this.$emit('question-renderer:replace', this.question);
    },
    removeQuestion(question) {
      this.removeFormQuestion(question);
    },
    stripHtmlTags(string) {
      return string.replace(/(<([^>]+)>)/ig, ' ');
    },
    getNumberArray(start, end, increment = 1) {
      // Helper funciton can probably be added to a utlitiies file or the store.
      // Taken from SMS code base to genenerate years
      const len = Math.floor((end - start) / increment) + 1;
      // Get numbers and add 0 to any number that's less than 2
      return Array(len).fill().map((_, index) => String(start + (index * increment)).padStart(2, 0));
    },
  },

};
</script>

<style lang="scss" scoped>

.Actions-menu {
  .QuestionUuid {
    background: #046FF0;
    color: white;
    padding: 2px 15px;
    text-transform: capitalize;
    margin-bottom: -2px;
    font-size: .8rem;
  }
  .RemoveButton {
    cursor: pointer;
    border-radius: 50%;
    color: white;
    background-color: #F10000;
    font-size: .9rem;
    height: 24px;
    width: 24px;
    margin-left: 5px;
  }
  .ReplaceButton {
    color: white;
    border-radius: 50%;
    height: 24px;
    width: 24px;
    background-color: #79848C;
  }
}

.Question-container {
    position: relative;
    margin: 2px 0;
    padding: 8px 10px;

  .DynamicLabel {
    font-size: 18px;
    width: 96%;
    word-break: break-word;
    color: var(--formTextQuestionColor);
   ::v-deep &.Required >:last-child::after {
      content: '*';
      color: red;
      margin-left: 4px;
    }

    &.IsHidden >:last-child::after {
      content: '\f070';
    }

    h1, h2, h3, h4, h5, h6 {
      margin: 0;
      line-height: initial;
    }
    ::v-deep p {
      margin: 0;
      color: var(--formTextQuestionColor);
    }
  }

  ul {
    margin: 0;
  }

  .Question-inputs {
    display: flex;
    align-items: center;

    .ParagraphText, .HeaderText {
      max-width: 100%;
      word-break: break-word;
    }
    .ParagraphText {
      padding: 5px 10px;
    }

    .Question-input-type {
        color: var(--formTextAnswerColor);
        flex-grow: 1;

        .ParagraphText, .HeaderText {
          display: inline-block;
        }

        ::v-deep h1,h2,h3, p {
          margin: 0;
          color: var(--formTextAnswerColor);
        }

        .HiddenQuestion, .ConditionalLogicIcon, .ConditionalLogic-container {
          color: #47505a7a;
          position: absolute;
          right: 4px;
          top: 4px;
        }

      .HiddenQuestion {
        right: 22px;
      }

        .BrandingImage {
          position: absolute;
            &.disabled {
              color: lightgray;
              pointer-events: none;
          }
        }
    }
  }

  .Icon.arrow-left {
    display:none;
  }

  &.Selected {
    border: 1px solid #046FF0;
    border-radius: 3px;
    border-top-left-radius: 0;
    background: rgba(4,111,240,0.05);

  }

  select {
    margin-top: -20px;
  }

  .Icon-grip-vertical {
    flex-basis: 20px;
    font-size: 17px;
    align-self: center;
    color: #d3dfe2;
  }

  .FileUpload-container {
    display: flex;
    margin: 20px 0;
    width: 90%;
    background: var(--formBackgroundColor);
    color: var(--formTextQuestionColor);
    border-radius: 4px;
    flex-direction: column;
    align-items: center;
    padding: 10px 0;
    text-align: center;

    .Icon-s {
      font-size: 1.4rem;
      margin-bottom: 10px;
    }
  }
}

::v-deep .RichHtml {
    margin-top: 5px;
    font-size: 1rem;

    h1, h2, h3, h4, h5, h6 {
      margin: 0;
      line-height: initial;
      color: var(--formTextAnswerColor);
    }
    p {
      font-size: 1rem;
      margin: 0;
      color: var(--formTextAnswerColor);
    }

  ul{
    display: inline-block;
    text-align: left;
    list-style: disc;
  }

  ol{
    list-style: auto;
  }

  ul,ol{
    margin-left: 40px;
  }
}
</style>
